"use client";

import React, { useEffect, useRef, useState, useMemo } from "react";
import { ClipboardList, Users, UserCheck, ShieldCheck, Rocket } from "lucide-react";
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from "@/components/ui/card";
type UseRevealOnScrollOptions = {
  once?: boolean;
  threshold?: number | number[];
  rootMargin?: string;
};
function useRevealOnScroll<T extends HTMLElement = HTMLElement>({
  once = true,
  threshold = 0.2,
  rootMargin = "0px"
}: UseRevealOnScrollOptions = {}) {
  const ref = useRef<T | null>(null);
  const [isVisible, setIsVisible] = useState(false);

  // Memoize observer options to prevent dependency issues
  const observerOptions = useMemo(() => ({
    threshold,
    rootMargin
  }), [threshold, rootMargin]);
  useEffect(() => {
    const node = ref.current;
    if (!node) {
      return;
    }
    if (typeof window === "undefined" || typeof IntersectionObserver === "undefined") {
      setIsVisible(true);
      return;
    }
    const observer = new IntersectionObserver(entries => {
      const entry = entries[0];
      if (entry?.isIntersecting) {
        setIsVisible(true);
        if (once) {
          observer.unobserve(entry.target);
        }
      } else if (!once) {
        setIsVisible(false);
      }
    }, observerOptions);
    observer.observe(node);
    return () => {
      observer.disconnect();
    };
  }, [once, observerOptions]);
  return {
    ref,
    isVisible
  };
}
const steps = [{
  title: "Describe Your Vibe",
  description: "Tell our AI what you're building. From a cozy bakery to a high-tech SaaS, just type your vision and watch the magic begin.",
  icon: ClipboardList
}, {
  title: "AI Generates the Magic",
  description: "Our engine builds your custom WordPress site in seconds, handling the layout, content, and images with professional precision.",
  icon: Users
}, {
  title: "Launch your Website",
  description: "Review your new digital home, make any final tweaks with drag-and-drop ease, and go live to the world instantly.",
  icon: Rocket
}];
export default function ProcesoDeReclutamiento() {
  const {
    ref: headerRef,
    isVisible: isHeaderVisible
  } = useRevealOnScroll<HTMLDivElement>();
  const {
    ref: gridRef,
    isVisible: isGridVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  return <section data-section-id="254336" id="from-prompt-to-published" className="py-16 md:py-24 dark bg-background text-foreground overflow-hidden">
      <div className="container mx-auto px-4 md:px-6 max-w-7xl">
        <div ref={headerRef} className={`max-w-3xl mx-auto text-center mb-12 md:mb-16 transition-all duration-700 ease-out ${isHeaderVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <h2 className="text-3xl md:text-5xl font-bold font-default text-foreground mb-4 tracking-tight">
            From Prompt to Published
          </h2>
          <p className="text-lg text-muted-foreground font-default">
            The simplest way to create a high-converting WordPress website. No coding, no stress—just your vibe turned into reality.
          </p>
        </div>

        <div ref={gridRef} className={`grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 transition-all duration-1000 ease-out delay-200 ${isGridVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"}`}>
          {steps.map((step, i) => {
          const Icon = step.icon;
          return <Card key={i} data-index={i} className="border border-white/10 shadow-lg hover:shadow-xl transition-all duration-300 bg-card rounded-2xl h-full flex flex-col relative overflow-hidden">
                <CardHeader className="pb-4">
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center justify-center w-12 h-12 rounded-lg bg-primary/20 text-primary font-bold text-xl font-default">
                      {i + 1}
                    </div>
                    <div className="p-2 rounded-lg bg-primary/10 text-primary">
                      <Icon className="w-6 h-6" />
                    </div>
                  </div>
                  <CardTitle className="text-2xl font-default text-foreground">
                    {step.title}
                  </CardTitle>
                </CardHeader>
                <CardContent className="pt-0 flex-grow">
                  <CardDescription className="text-base text-muted-foreground font-default leading-relaxed">
                    {step.description}
                  </CardDescription>
                </CardContent>
                {}
                <div className="absolute bottom-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-primary to-transparent opacity-30"></div>
              </Card>;
        })}
        </div>
      </div>
    </section>;
}